import { ConvertingStatus } from "@basic-document-viewer/api/build/domain/documents";
import { FailureReason } from "@basic-document-viewer/api/build/domain/failure-reasons";
import EventEmitter from "eventemitter3";
import retry, { TimeoutsOptions } from "retry";

export interface PdfService {
  load(
    tenantId: string,
    hash: string,
    encodedOriginalUrl: string,
  ): Promise<PdfEmitter>;
}

export type PdfStateName = "loading" | ConvertingStatus;

interface PdfEmitterEvents {
  loading: () => void;
  converting: () => void;
  done: (pdfUrl: string, requiredConversion: boolean) => void;
  failed: (failureReason: FailureReason) => void;
}

interface PdfEmitterListeners {
  on<U extends keyof PdfEmitterEvents>(
    event: U,
    listener: PdfEmitterEvents[U],
  ): this;
  emit<U extends keyof PdfEmitterEvents>(
    event: U,
    ...args: Parameters<PdfEmitterEvents[U]>
  ): boolean;
}

class PdfEmitter extends EventEmitter implements PdfEmitterListeners {
  converting(): void {
    this.emit("converting");
  }
  done(pdfUrl: string, requiredConversion?: boolean): void {
    this.emit("done", pdfUrl, requiredConversion);
  }
  failed(failureReason: FailureReason): void {
    this.emit("failed", failureReason);
  }
}

class LocalDevelopmentPdfService implements PdfService {
  private emitter = new PdfEmitter();

  public constructor(
    private readonly convertTimeout: number = 2000,
    private readonly doneTimeout: number = convertTimeout + 2000,
  ) {}

  load(
    tenantId: string,
    hash: string,
    originalUrl: string,
  ): Promise<PdfEmitter> {
    // Repurpose the original URL as a PDF url in local development
    setTimeout(() => this.emitter.converting(), this.convertTimeout);
    // setTimeout(
    //   () => this.emitter.failed(FailureReason.CouldNotConvert),
    //   this.doneTimeout
    // );
    setTimeout(() => this.emitter.done(originalUrl, false), this.doneTimeout);

    return Promise.resolve(this.emitter);
  }
}

export type ApiResponse = {
  status: PdfStateName;
  failureReason?: FailureReason;
  requiredConversion?: boolean;
  urls: {
    pdf?: string;
    poll?: string;
  };
};

export class ApiPdfService implements PdfService {
  private emitter = new PdfEmitter();

  /** A set of timeouts to respect when polling the REST API */
  private timeouts: number[];

  public constructor(
    timeoutsOptions: TimeoutsOptions = {
      retries: 50,
      factor: 1.5,
      maxTimeout: 30000,
    },
  ) {
    // 50 exponentially backing off retries with a cap of 30 seconds produces:
    // 1000, 1500, 2250, 3375, 5063, 7594, 11391, 17086, 25629, 30000, 30000, 30000, ...
    this.timeouts = retry.timeouts(timeoutsOptions);
  }

  public async load(
    tenantId: string,
    hash: string,
    encodedOriginalUrl: string,
  ): Promise<PdfEmitter> {
    // Do not await this.
    this.checkApi(
      `/api/v1/tenants/${tenantId}/documents/${hash}?url=${encodedOriginalUrl}`,
    );

    return Promise.resolve(this.emitter);
  }

  private async checkApi(url: string): Promise<void> {
    const data = await fetch(url);
    if (data.status !== 200) {
      this.emitter.emit("failed", FailureReason.UrlInvalid);
      return;
    }
    const json: ApiResponse = await data.json();
    if (json.status === "done") {
      if (json.urls.pdf === undefined) {
        throw new Error(
          "The API indicated the conversion was done, but did not return a URL to the PDF",
        );
      }

      this.emitter.done(json.urls.pdf, json.requiredConversion);
    } else if (json.status === "failed") {
      this.emitter.emit("failed", json.failureReason);
    } else if (json.status === "converting") {
      this.emitter.emit("converting");
      const pollUrl = json.urls.poll;
      if (pollUrl === undefined) {
        throw new Error(
          "The API indicated the document is being converted, but did not return a pollable URL",
        );
      }

      if (this.timeouts.length > 0) {
        const nextTimeout = this.timeouts.shift();
        setTimeout(() => this.checkApi(pollUrl), nextTimeout);
      } else {
        // The API will no longer be checked. With the default settings of 50 retries, the user has already been waiting
        // 20+ minutes. It's unlikely that any further polling would be successful or even be seen by the user. Emit
        // a failed response
        this.emitter.emit("failed");
      }
    }
  }
}

export function getPdfService(): PdfService {
  if (window.location.host.indexOf("localhost") !== -1) {
    // Local development
    return new LocalDevelopmentPdfService();
  } else {
    // Deployed app
    return new ApiPdfService();
  }
}
